import { useCallback } from 'react'
import omit from 'lodash/omit'
import { useAppSelector } from 'store/hooks'
import useBarryViewState from 'components/Barry/hooks/useBarryController '
import { differenceInMonths } from 'date-fns'
import useGetUser from 'components/Dashboard/utils/useGetUser'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { BarryPopUpMessagAction } from 'components/Barry/BarryComponents/BarryPopUp'
import useQueryString from 'utils/useQueryString'

export default function useBarryDashboard(): {
  handleDashboardDialogToggle: () => void
  handleProvisioningHelpPopupInteraction: (action: BarryPopUpMessagAction) => void
  hasProvisioningHelpPopup: boolean
} {
  const {
    isBarryDialogOpen,
    dismissProvisioningHelpPopup,
    setBarryIconVisibility,
    setBarryDialogOpen,
  } = useBarryViewState()
  const { qs, nav } = useQueryString()
  const { hasProvisioningHelpPopup } = useProvisioningHelpPopupState()

  const handleDashboardDialogToggle = useCallback(() => {
    if (!isBarryDialogOpen && hasProvisioningHelpPopup) {
      dismissProvisioningHelpPopup()
    }

    if (qs.barry) {
      nav({ ...omit(qs, 'barry') })
    }

    setBarryDialogOpen(!isBarryDialogOpen)
  }, [
    isBarryDialogOpen,
    hasProvisioningHelpPopup,
    dismissProvisioningHelpPopup,
    nav,
    qs,
    setBarryDialogOpen,
  ])

  const handleProvisioningHelpPopupInteraction = useCallback(
    (action: BarryPopUpMessagAction) => {
      //Clicking close button on provision popup should close the window, and clicking the text should have no effect
      if (action === BarryPopUpMessagAction.HIDE) {
        dismissProvisioningHelpPopup()
        setBarryIconVisibility(false)
      }
    },
    [dismissProvisioningHelpPopup, setBarryIconVisibility],
  )

  return {
    handleDashboardDialogToggle,
    handleProvisioningHelpPopupInteraction,
    hasProvisioningHelpPopup,
  }
}

function useProvisioningHelpPopupState(): {
  hasProvisioningHelpPopup: boolean
  showProvisionOnMobile: boolean
} {
  const { data: user } = useGetUser()
  const isMobile = useBreakpointIndex() === 0
  const isOrganization = useAppSelector(s => s.session.isOrganization)
  const { openProvisionPopup, hasProvisionPopupBeenDismissed } = useAppSelector(
    s => s.barry.userExperience,
  )
  const { qs } = useQueryString()

  const isLessThan2MonthsOld =
    !!user?.date && differenceInMonths(new Date(), new Date(user.date)) < 2

  const hasProvisioningHelpPopup =
    isOrganization &&
    isLessThan2MonthsOld &&
    hasProvisionPopupBeenDismissed === undefined &&
    openProvisionPopup

  const showProvisionOnMobile = !!qs.provisionDialog && isMobile && hasProvisioningHelpPopup

  return { hasProvisioningHelpPopup, showProvisionOnMobile }
}
